import { LoginMethodBadgeVariant } from '@/components/LoginMethodBadge/LoginMethodBadge.vue';
import { CompanyType } from '@/components/PersonInfo/PersonInfo.vue';
import { UserCardActivationStatus } from '@/components/UserCard/UserCard.vue';
import {
    GetUsersWithLoginDataQuery,
    PasswordLoginData,
    SsoLoginData,
    SsoProvider,
    UserWithLoginData,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';

export type MappedSsoLogin = SsoLoginData & {
    tooltipText: string;
    badgeVariant: LoginMethodBadgeVariant;
};
export interface MappedUser {
    id: string;
    isActive: boolean;
    firstName: string;
    lastName: string;
    fullName: string;
    activationStatus: UserCardActivationStatus;
    isAdmin: boolean;
    inactiveSsoLogins: { id: string; provider: SsoProvider; email: string }[];
    passwordLogin?: PasswordLoginData | null;
    ssoLogins: MappedSsoLogin[];
    provider?: SsoProvider | null;
    avatarUrl?: string;
    jobTitle?: string;
    company?: { name: string; type: CompanyType };
    activatedAt?: string;
    deactivatedAt?: string;
}

function getActivationStatus(user: UserWithLoginData): UserCardActivationStatus {
    if (user.provider) {
        return 'Inactive';
    }
    if (!user.provider && !user.isActive) {
        return 'Deactivated';
    }
    return 'Active';
}

function getCompany(user: UserWithLoginData) {
    const name = user.company ?? '';
    if (name === '') return undefined;
    let type = 'Other';
    if (name === 'Koelnmesse') {
        type = 'Koelnmesse';
    }
    if (name === 'Koelncongress') {
        type = 'Koelncongress';
    }
    return { name, type: type as CompanyType };
}

export function mapSsoLogin(ssoLogin: SsoLoginData) {
    return {
        ...ssoLogin,
        tooltipText: $t('login-sso-tooltip-text', { provider: ssoLogin.provider }),
        badgeVariant: ssoLogin.provider === SsoProvider.SsoKm ? ('Koelnmesse' as const) : ('Sky' as const),
    };
}

export function mapUser(user: UserWithLoginData): MappedUser {
    return {
        id: user.id,
        isActive: user.isActive,
        firstName: user.firstName,
        lastName: user.lastName,
        fullName: `${user.firstName} ${user.lastName}`,
        activationStatus: getActivationStatus(user),
        isAdmin: user.isAdmin,
        inactiveSsoLogins: user.matchingEntraUsers.filter(
            (m) => !user.ssoLogins.map((l) => l.provider).includes(m.provider),
        ),
        passwordLogin: user.passwordLogin,
        ssoLogins: user.ssoLogins.map((sso) => mapSsoLogin(sso)),
        provider: user.provider,
        avatarUrl: user.avatarUrl ?? undefined,
        company: getCompany(user),
        jobTitle: user.jobTitle ?? '',
        activatedAt: user.activatedAt ? new Date(user.activatedAt).toLocaleDateString() : undefined,
        deactivatedAt: user.deactivatedAt ? new Date(user.deactivatedAt).toLocaleDateString() : undefined,
    };
}

export function mapUsers(data: GetUsersWithLoginDataQuery) {
    return data.usersWithLoginData
        .map(mapUser)
        .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
}
